// Illinois Institute of Technology: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - The first-year counts come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024, first-time degree-seeking students
//    (College Navigator and the ADM2024 complete data file, unit 145725:
//    APPLCN 12,216, ADMSSN 6,710, ENRLT 519). Departure: Illinois Tech's
//    Office of Institutional Insights Common Data Set page posts editions
//    only through 2023-2024 (Fall 2023), so IPEDS Fall 2024 is the newer
//    official count and no CDS figure is used. IPEDS has no residency split
//    and no early decision counts, so the page shows neither.
//  - iit.edu Undergraduate Admission: Application Dates and Deadlines (the
//    first-year table, the binding note and the dual admission programs),
//    First-Year Students (optional tests and letters, superscoring, the four
//    decision types) and Recommended Admission Guidelines (program criteria).
//    Quirk: these pages carry no year in the first-year table, and on
//    2026-10-01 the First-Year Students page still said "Application for
//    academic year 2026 are now open" and the deadlines page still named the
//    fall 2026 term for transfer scholarships. The dates are the ones posted
//    on that day; the deadlines note says to confirm them.
//  - Office of Financial Aid, Tuition, Fees, and Costs: the 2026-27 cost of
//    attendance for new undergraduate students living on campus (loan fees
//    included, as Illinois Tech lists them).
//  - College Scorecard, school id 145725 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=145725#admsns',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Illinois Tech Undergraduate Admission, Application Dates and Deadlines',
        url: 'https://www.iit.edu/admissions-aid/undergraduate-admission/application-dates-and-deadlines',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Illinois Tech Undergraduate Admission, First-Year Students',
        url: 'https://www.iit.edu/admissions-aid/undergraduate-admission/first-year-students',
        read: '2026-10-01',
    },
    guidelines: {
        label: 'Illinois Tech Undergraduate Admission, Recommended Admission Guidelines',
        url: 'https://www.iit.edu/admissions-aid/undergraduate-admission/first-year-students/recommended-admission-guidelines',
        read: '2026-10-01',
    },
    cost: {
        label: 'Illinois Tech Office of Financial Aid, Tuition, Fees, and Costs',
        url: 'https://www.iit.edu/financial-aid/tuition-fees-and-costs',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?145725-Illinois-Institute-of-Technology',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'illinois-institute-of-technology',
    kbId: 'illinois_institute_of_technology',
    name: 'Illinois Institute of Technology',
    shortName: 'Illinois Tech',
    aliases: ['Illinois Tech'],
    location: 'Chicago, IL',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (iit.edu university colors: Illinois Tech Red, Illinois Tech Gray); decoration only.
    branding: {
        primary: '#CC0000',
        secondary: '#76777B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/illinois_institute_of_technology_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 12216,
        admitted: 6710,
        enrolled: 519,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Illinois Tech publishes no first-year acceptance rate by school or major, and its newest Common Data Set is the 2023-2024 edition, so these counts come from the federal IPEDS survey for fall 2024.',
        source: 'ipeds',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You can name a first- and second-choice major, or apply to Discover+ (undecided) and choose a major at the end of your second year. Some parts of the application work differently:',
        rows: [
            { school: 'STEM majors', requirement: 'Engineering, computing, the sciences and math may have different requirements from architecture, business and the humanities. For the STEM track Illinois Tech recommends four years of math (pre-calculus or higher highly recommended) and four years of science (physics or AP Physics highly recommended).', source: 'guidelines' },
            { school: 'Admission decisions', requirement: 'Admitted students get direct admission to their first- or second-choice program, or university admission into Discover+ with the option to reapply to a preferred major in the second year or take one of the majors offered in the decision email.', source: 'firstYear' },
            { school: 'Dual admission and early acceptance programs', requirement: 'Medicine, law, pharmacy, dentistry and optometry programs with partner schools may need a supplemental application or an interview, with their own deadlines from November 1 to March 1.', source: 'deadlines' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'December', binding: true },
            { plan: 'Early Action', deadline: 'November 15', decision: 'January', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'March 15', binding: false },
        ],
        note: 'Enrollment deposits are due March 15 (Early Decision) and May 1 (Early Action, Regular Decision). International students living in the U.S. may apply Regular Decision until June 1. The dates carry no year, and on October 1, 2026 Illinois Tech\'s first-year page still described the fall 2026 application, so confirm them with the admission office.',
        source: 'deadlines',
        binding: 'Early Decision is binding: if accepted, you must withdraw all applications from other universities. Illinois Tech invites Early Decision applications from students for whom it is the first choice regardless of financial need, and suggests Early Action instead if cost is a factor.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Illinois Tech does not require standardized tests for undergraduate applicants, for admission or for institutional scholarships. If you send SAT or ACT scores, they are reviewed, and Illinois Tech superscores official scores.',
        source: 'firstYear',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 53198 },
            { item: 'Required fees', amount: 1850 },
            { item: 'Housing (on campus)', amount: 9776 },
            { item: 'Food (meal plan)', amount: 8772 },
            { item: 'Books and supplies', amount: 1340 },
            { item: 'Transportation', amount: 1350 },
            { item: 'Personal expenses', amount: 3500 },
            { item: 'Loan fees', amount: 902 },
        ],
        total: 80688,
        note: 'Estimated cost for a new undergraduate living on campus. Fees cover the Student Service Fee and Activity Fee; lab fees and university health insurance are not included.',
        source: 'cost',
        where: 'New Undergraduate Students, Living on Campus',
        netPrice: {
            average: 22443,
            byIncome: [
                { income: '$0 to $30,000', amount: 17590 },
                { income: '$30,001 to $48,000', amount: 17829 },
                { income: '$48,001 to $75,000', amount: 18136 },
                { income: '$75,001 to $110,000', amount: 26979 },
                { income: 'Over $110,000', amount: 35019 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Illinois Tech\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
